<template>
  <section class="sil-wrapper" v-loading="loading">
    <!-- KPI cards -->
    <div class="sil-cards">
      <div class="sil-card">
        <span class="sil-card__label">Base imponible</span>
        <FormatAmount :amount="sales.total_sales" class="sil-card__value" />
      </div>
      <div class="sil-card sil-card--credit">
        <span class="sil-card__label">Notas de crédito</span>
        <FormatAmount
          :amount="sales.total_credit_notes"
          class="sil-card__value"
        />
      </div>
      <div class="sil-card sil-card--total">
        <span class="sil-card__label">Total gravado</span>
        <FormatAmount :amount="sales.total_net_sales" class="sil-card__value" />
      </div>
      <div class="sil-card sil-card--tax">
        <span class="sil-card__label">IVA 15%</span>
        <FormatAmount
          :amount="sales.total_tax"
          class="sil-card__value sil-card__value--accent"
        />
      </div>
    </div>

    <!-- Filtro de fechas -->
    <div class="dir-toolbar">
      <FilterBetweenDatesSelect
        :defaultMonthValue="true"
        @onAsyncDataForDates="onAsyncDataForDates"
      />
      <div class="dir-toolbar-right">
        <el-tooltip
          v-if="sales.total_net_sales || sales.total_net_sales === 0"
          content="Descargar Excel"
          placement="top"
        >
          <button class="dir-view-toggle" @click="downloadFile">
            <i class="el-icon-document"></i>
          </button>
        </el-tooltip>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-if="!loading && !sales.total_net_sales && sales.total_net_sales !== 0"
      class="sil-empty"
    >
      <i class="el-icon-data-line"></i>
      <p>No hay datos de IVA para el período seleccionado</p>
    </div>

    <template v-if="sales.total_net_sales || sales.total_net_sales === 0">
      <!-- Tabla — desktop y mobile (scroll horizontal en pantallas pequeñas) -->
      <div class="sil-table-wrap">
        <div class="sil-table">
          <div class="sil-table__header">
            <div class="sil-table__cell sil-table__cell--wide">Impuesto</div>
            <div class="sil-table__cell">Base imponible</div>
            <div class="sil-table__cell">Notas de crédito</div>
            <div class="sil-table__cell">Total gravado</div>
            <div class="sil-table__cell">IVA total</div>
          </div>
          <div class="sil-table__row">
            <div class="sil-table__cell sil-table__cell--wide">
              <span class="sil-badge">IVA 15%</span>
            </div>
            <div class="sil-table__cell">
              <FormatAmount :amount="sales.total_sales" />
            </div>
            <div class="sil-table__cell">
              <FormatAmount :amount="sales.total_credit_notes" />
            </div>
            <div class="sil-table__cell">
              <FormatAmount :amount="sales.total_net_sales" />
            </div>
            <div class="sil-table__cell sil-table__cell--accent">
              <FormatAmount :amount="sales.total_tax" />
            </div>
          </div>
        </div>
        <!-- /.sil-table -->
      </div>
      <!-- /.sil-table-wrap -->
    </template>
  </section>
</template>

<script>
import reportService from "../services/reportService";
import FilterBetweenDatesSelect from "../../components/FilterBetweenDatesSelect";
import FormatAmount from "@/components/General/FormatAmount";

export default {
  name: "SalesIvaList",
  data() {
    return {
      sales: {},
      loading: false
    };
  },
  methods: {
    onAsyncDataForDates({ searchDates }) {
      this.searchDates = searchDates;
      this.sales = {};
      this.getSalesIva();
    },
    dateNow() {
      return new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/-/g, "_")
        .replace("T", "_");
    },
    async downloadFile() {
      this.loading = true;
      try {
        const response = await reportService
          .getDownloadIvaExcel({
            params: { ...this.searchDates },
            headers: {
              responseType: "blob",
              ContentType: "application/vnd.ms-excel"
            }
          })
          .finally(() => {
            this.loading = false;
          });

        const { data, success } = response.data;
        if (success) {
          const anchor = document.createElement("a");
          anchor.setAttribute("download", `${this.dateNow()}_reporte_iva.xlsx`);
          anchor.setAttribute("href", data);
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          this.$notifications.success({
            message: "Excel descargado exitosamente"
          });
        }
      } catch (error) {
        return false;
      }
    },
    async getSalesIva() {
      try {
        this.loading = true;
        const response = await reportService.getSalesProducts({
          params: { ...this.searchDates, _report: "iva" }
        });

        if (response && response.data && response.data.success) {
          this.sales = response.data.data;
        }
      } catch (error) {
        /* silent */
      } finally {
        this.loading = false;
      }
    }
  },
  components: {
    FilterBetweenDatesSelect,
    FormatAmount
  },
  mounted() {
    // Initial data load handled by FilterBetweenDatesSelect -> onAsyncDataForDates
  }
};
</script>

<style lang="scss" scoped>
$primary: #2d2b55;
$accent: #3ecfb4;
$border: #eef0f8;

.sil-wrapper {
  padding: 4px 0;
}

/* ── Toolbar — uses global .dir-toolbar from main.scss ── */

/* ── Cards ── */
.sil-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.sil-card {
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  &__label {
    font-size: 11px;
    color: #aaa;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__value {
    font-size: 20px;
    font-weight: 700;
    color: $primary;

    @media (min-width: 768px) {
      font-size: 22px;
    }

    &--accent {
      color: $accent;
    }
  }

  &--tax {
    border-color: rgba($accent, 0.35);
    background: rgba($accent, 0.04);
  }

  &--total {
    border-color: rgba($primary, 0.15);
  }
}

/* ── Tabla (todas las pantallas, scroll horizontal en mobile) ── */
.sil-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 12px;
}

.sil-table {
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 12px;
  overflow: hidden;
  min-width: 520px; // fuerza scroll en pantallas < 520px

  &__header {
    display: flex;
    background: #f7f8fb;
    border-bottom: 1.5px solid $border;
    padding: 10px 16px;
  }

  &__row {
    display: flex;
    padding: 14px 16px;
    align-items: center;

    &:hover {
      background: #fafbff;
    }
  }

  &__cell {
    flex: 1;
    font-size: 13px;
    font-weight: 500;
    color: $primary;
    min-width: 90px;

    .sil-table__header & {
      font-size: 11px;
      font-weight: 600;
      color: #aaa;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    &--wide {
      flex: 1.5;
      min-width: 110px;
    }

    &--accent {
      color: $accent;
      font-weight: 700;
    }
  }
}

.sil-badge {
  display: inline-block;
  background: rgba($accent, 0.12);
  color: darken($accent, 10%);
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
}

/* ── Empty ── */
.sil-empty {
  text-align: center;
  padding: 60px 20px;
  color: #aaa;

  i {
    font-size: 40px;
    color: #c0c4d0;
    display: block;
    margin-bottom: 12px;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
}
</style>
